import React from 'react';

import { useAppSelector } from 'flow/hooks/useRedux';
import FromAvatar from 'flow/pages/messages/FromAvatar';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ChatSession } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { getBgColorFromKey_TW, getChatRoomAvatar } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import Skeleton from './Skeleton';
import UnreadBadge from './UnreadBadge';
import UserAvatar from './UserAvatar';

interface Props {
    session?: TypedMap<ChatSession>;
    size?: AvatarSize;
    showUnreadBadge?: boolean;
}

export type AvatarSize =
    | 'tiny'
    | 'extraSmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'extraLarge'
    | 'videoLarge'
    | 'toolbar'
    | 'meetingTile';

const ChatAvatar = ({ session, size, showUnreadBadge = true }: Props) => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    if (!session) {
        return (
            <Skeleton>
                <Skeleton.Avatar />
            </Skeleton>
        );
    }

    if (session?.get('roomImageResource')) {
        return (
            <div className="relative flex items-center justify-center rounded-full bg-gray-200">
                <UserAvatar
                    src={getChatRoomAvatar(session?.get('roomImageResource'))}
                    size={size}
                    lazyLoad={true}
                    className="rounded-xl"
                />
                {showUnreadBadge && <UnreadBadge session={session} />}
            </div>
        );
    }

    if (session?.get('isChatRoom') || session?.get('members').size > 2) {
        return (
            <div
                className={twMerge(
                    'relative flex shrink-0 items-center justify-center rounded-xl',
                    getBgColorFromKey_TW(session.get('key')),
                    size === 'large' ? 'size-14' : 'size-10',
                )}
            >
                <span
                    className={twMerge(
                        'font-bold text-white',
                        size === 'large' ? 'text-lg' : 'text-sm',
                    )}
                >
                    {session?.get('members').size}
                </span>

                {showUnreadBadge && <UnreadBadge session={session} />}
            </div>
        );
    }

    const member = session
        ?.get('members')
        .find((m) => m.get('extensionKey') !== ownExtensionKey);

    return (
        <div className="relative flex items-center justify-center rounded-full bg-gray-200">
            <FromAvatar
                from={member}
                size={size}
                chatSessionKey={session.get('key')}
            />
            {showUnreadBadge && <UnreadBadge session={session} />}
        </div>
    );
};

export default ChatAvatar;
